import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type {
  MailConfig,
  MailHtmlMode,
  MailNotification,
  MailNotificationList,
  MailNotificationStatus,
  MailTemplate,
  MailTemplateCreate,
  MailTemplateList,
  MailTemplatePreview,
  MailTemplatePreviewRequest,
  MailTemplateTrigger,
  MailTemplateUpdate,
  MailTestRequest,
  MailTestResult,
  EffectiveRecipients,
  NotificationRecipients,
  NotificationRecipientsUpdate,
} from '@/types/api';

/**
 * Hooks für die Mail-Domain: SMTP-Konfiguration, Vorlagen, Postausgang
 * und Empfänger-Verwaltung pro Kunde.
 *
 * Muster wie useCustomers/useContracts: Query-Key-Factories aus
 * `@/lib/query`, Mutations mit gezielter Cache-Invalidation. Typen sind
 * handgepflegte Interfaces in `@/types/api` (Backend entsteht parallel,
 * gebaut strikt gegen den abgestimmten API-Vertrag).
 */

// --- SMTP-Konfiguration ----------------------------------------------------

export function useMailConfig(): ReturnType<typeof useQuery<MailConfig>> {
  return useQuery<MailConfig>({
    queryKey: queryKeys.mail.config,
    queryFn: () => api.get<MailConfig>('/mail/config'),
  });
}

/** Testmail an redirect/SMTP_USER oder eine explizit angegebene Adresse. */
export function useSendTestMail(): ReturnType<
  typeof useMutation<MailTestResult, Error, MailTestRequest>
> {
  return useMutation<MailTestResult, Error, MailTestRequest>({
    mutationFn: (body) => api.post<MailTestResult, MailTestRequest>('/mail/test', body),
  });
}

// --- Vorlagen --------------------------------------------------------------

export function useMailTemplates(): ReturnType<typeof useQuery<MailTemplateList>> {
  return useQuery<MailTemplateList>({
    queryKey: queryKeys.mail.templates,
    queryFn: () => api.get<MailTemplateList>('/mail-templates'),
  });
}

export function useCreateMailTemplate(): ReturnType<
  typeof useMutation<MailTemplate, Error, MailTemplateCreate>
> {
  const qc = useQueryClient();
  return useMutation<MailTemplate, Error, MailTemplateCreate>({
    mutationFn: (body) => api.post<MailTemplate, MailTemplateCreate>('/mail-templates', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mail.templates });
    },
  });
}

export interface UpdateMailTemplateInput {
  templateId: string;
  body: MailTemplateUpdate;
}

export function useUpdateMailTemplate(): ReturnType<
  typeof useMutation<MailTemplate, Error, UpdateMailTemplateInput>
> {
  const qc = useQueryClient();
  return useMutation<MailTemplate, Error, UpdateMailTemplateInput>({
    mutationFn: ({ templateId, body }) =>
      api.patch<MailTemplate, MailTemplateUpdate>(`/mail-templates/${templateId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mail.templates });
    },
  });
}

/** Seed-Vorlagen liefern 409 - Backend erlaubt nur Deaktivieren. */
export function useDeleteMailTemplate(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (templateId) => api.delete<void>(`/mail-templates/${templateId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.mail.templates });
    },
  });
}

export interface TemplatePreviewInput {
  templateId: string;
  /** null = Vorschau mit Beispieldaten. */
  customerId: string | null;
  /**
   * Der aktuelle Stand aus dem Editor. Weglassen heißt "rendere die
   * gespeicherte Fassung" - was für eine Vorschau fast nie gemeint ist.
   */
  draft?: {
    trigger: MailTemplateTrigger;
    /** Überschrift im Kopfband des abgeleiteten HTML-Teils. */
    name: string;
    subjectTemplate: string;
    bodyTemplate: string;
    bodyHtmlTemplate: string | null;
    htmlMode: MailHtmlMode;
  };
}

/**
 * Rendert eine Vorlage serverseitig (sandboxed Jinja2) - auf Knopfdruck.
 *
 * Für die laufende Vorschau im Editor gibt es `useLiveTemplatePreview`; diese
 * Mutation bleibt für Fälle, in denen ein Nutzer das Rendern ausdrücklich
 * auslöst.
 *
 * Mit `draft` wird der BEARBEITETE Stand gerendert, ohne ihn der gespeicherte.
 * Vorher konnte der Hook nur Letzteres, und die Vorschau im Editor zeigte
 * jede ungespeicherte Änderung schlicht nicht an (Befund 17.08.2026).
 */
export function useTemplatePreview(): ReturnType<
  typeof useMutation<MailTemplatePreview, Error, TemplatePreviewInput>
> {
  return useMutation<MailTemplatePreview, Error, TemplatePreviewInput>({
    mutationFn: ({ templateId, customerId, draft }) =>
      api.post<MailTemplatePreview, MailTemplatePreviewRequest>(
        `/mail-templates/${templateId}/preview`,
        {
          customer_id: customerId,
          ...(draft
            ? {
                trigger: draft.trigger,
                name: draft.name,
                subject_template: draft.subjectTemplate,
                body_template: draft.bodyTemplate,
                body_html_template: draft.bodyHtmlTemplate,
                html_mode: draft.htmlMode,
              }
            : {}),
        }
      ),
  });
}

/**
 * LAUFENDE Vorschau: rendert automatisch, sobald sich die Eingabe beruhigt hat.
 *
 * Warum als Query und nicht als Mutation (Befund 18.08.2026): Eine Vorschau
 * ist ein reiner Lesevorgang - sie ändert nichts, nur der Request ist ein
 * POST (der Entwurf muss in den Body). Als Query übernimmt React Query das
 * Verwerfen überholter Antworten: tippt jemand weiter, während eine ältere
 * Anfrage noch läuft, kann deren Ergebnis die neuere nicht mehr
 * überschreiben. Mit einer Mutation müsste man das von Hand zählen.
 *
 * `keepPreviousData` hält die letzte gerenderte Fassung sichtbar, während
 * die nächste lädt - sonst flackert die Vorschau bei jedem Tastendruck weiß.
 *
 * Der Aufrufer debounced die Eingabe (siehe useDebouncedValue); dieser Hook
 * feuert für jeden Stand, den er bekommt.
 */
export function useLiveTemplatePreview(
  templateId: string | undefined,
  draft: TemplatePreviewInput['draft'],
  customerId: string | null,
  enabled = true
): ReturnType<typeof useQuery<MailTemplatePreview>> {
  return useQuery<MailTemplatePreview>({
    queryKey: [
      'mail-template-preview',
      templateId ?? '',
      customerId ?? '',
      draft?.trigger ?? '',
      draft?.name ?? '',
      draft?.subjectTemplate ?? '',
      draft?.bodyTemplate ?? '',
      draft?.bodyHtmlTemplate ?? '',
      draft?.htmlMode ?? '',
    ],
    queryFn: () =>
      api.post<MailTemplatePreview, MailTemplatePreviewRequest>(
        `/mail-templates/${templateId ?? ''}/preview`,
        {
          customer_id: customerId,
          ...(draft
            ? {
                trigger: draft.trigger,
                name: draft.name,
                subject_template: draft.subjectTemplate,
                body_template: draft.bodyTemplate,
                body_html_template: draft.bodyHtmlTemplate,
                html_mode: draft.htmlMode,
              }
            : {}),
        }
      ),
    enabled: enabled && Boolean(templateId),
    placeholderData: keepPreviousData,
    // Ein Syntaxfehler in der Vorlage ist ein 422 und wird sich ohne neue
    // Eingabe nicht von selbst heilen - Wiederholungen wären nur Last.
    retry: false,
    staleTime: Infinity,
  });
}

// --- Postausgang -----------------------------------------------------------

export interface NotificationsFilter {
  status?: MailNotificationStatus;
  customerId?: string;
  limit?: number;
}

export function useNotifications(
  filter: NotificationsFilter = {}
): ReturnType<typeof useQuery<MailNotificationList>> {
  const params: Record<string, string | number | undefined> = {
    'filter[status]': filter.status,
    'filter[customer_id]': filter.customerId,
    limit: filter.limit ?? 100,
  };
  return useQuery<MailNotificationList>({
    queryKey: queryKeys.notifications.list(filter as Record<string, unknown>),
    queryFn: () => api.get<MailNotificationList>('/notifications', params),
  });
}

/**
 * Anzahl offener Entwürfe für das Sidebar-Badge. Bewusst eigener
 * Query-Key + kleines limit; wird durch Send/Discard-Mutations über
 * `queryKeys.notifications.all` mit-invalidiert.
 */
export function useDraftNotificationCount(): number | undefined {
  const query = useQuery<MailNotificationList>({
    queryKey: queryKeys.notifications.draftCount,
    queryFn: () =>
      api.get<MailNotificationList>('/notifications', {
        'filter[status]': 'draft',
        limit: 1,
      }),
    refetchInterval: 60_000,
    // Badge ist Nice-to-have: Fehler leise schlucken, kein Retry-Sturm.
    retry: false,
  });
  return query.data?.meta.draft_count;
}

export function useSendNotification(): ReturnType<
  typeof useMutation<MailNotification, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<MailNotification, Error, string>({
    mutationFn: (notificationId) =>
      api.post<MailNotification>(`/notifications/${notificationId}/send`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

export function useDiscardNotification(): ReturnType<
  typeof useMutation<void, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (notificationId) =>
      api.post<void>(`/notifications/${notificationId}/discard`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

// --- Empfänger pro Kunde --------------------------------------------------

export function useNotificationRecipients(
  customerId: string | undefined
): ReturnType<typeof useQuery<NotificationRecipients>> {
  return useQuery<NotificationRecipients>({
    queryKey: queryKeys.customers.notificationRecipients(customerId ?? ''),
    queryFn: () =>
      api.get<NotificationRecipients>(
        `/customers/${customerId ?? ''}/notification-recipients`
      ),
    enabled: Boolean(customerId),
  });
}

/** Lese-Sicht "wer bekommt Wartungsmails" samt Grund je Adresse. */
export function useEffectiveRecipients(
  customerId: string | undefined
): ReturnType<typeof useQuery<EffectiveRecipients>> {
  return useQuery<EffectiveRecipients>({
    queryKey: queryKeys.customers.effectiveRecipients(customerId ?? ''),
    queryFn: () =>
      api.get<EffectiveRecipients>(
        `/customers/${customerId ?? ''}/notification-recipients/effective`
      ),
    enabled: Boolean(customerId),
  });
}

export interface UpdateNotificationRecipientsInput {
  customerId: string;
  body: NotificationRecipientsUpdate;
}

export function useUpdateNotificationRecipients(): ReturnType<
  typeof useMutation<NotificationRecipients, Error, UpdateNotificationRecipientsInput>
> {
  const qc = useQueryClient();
  return useMutation<NotificationRecipients, Error, UpdateNotificationRecipientsInput>({
    mutationFn: ({ customerId, body }) =>
      api.put<NotificationRecipients, NotificationRecipientsUpdate>(
        `/customers/${customerId}/notification-recipients`,
        body
      ),
    onSuccess: (data, { customerId }) => {
      qc.setQueryData(queryKeys.customers.notificationRecipients(customerId), data);
      // Opt-out entscheidet, ob überhaupt Mails rausgehen - sonst zeigt der
      // Kasten "Wer bekommt Wartungsmails" einen Stand von gestern.
      void qc.invalidateQueries({
        queryKey: queryKeys.customers.effectiveRecipients(customerId),
      });
    },
  });
}
